import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { Tags } from "@/core/components/tags/Tags";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { createRoute, ROUTES } from "@/core/constants/app";
import { imageList } from "@/core/utils/imageList";
import { RelatedCall } from "../model/types";

interface RelatedCallCardProps {
  call: RelatedCall;
  userBasicInfo: any;
  onBookmark: () => void;
}

export const RelatedCallCard: React.FC<RelatedCallCardProps> = ({
  call,
  userBasicInfo,
  onBookmark,
}) => {
  const daysLeft = useMemo(() => {
    const today = new Date();
    const deadlineDate = new Date(call.deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [call.deadline]);

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647"; // green
    if (66 >= score && score >= 34) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  return (
    <div className="related-call-section">
      <h3>Related call</h3>
      <div className="call-card">
        <Link to={createRoute(ROUTES.CALL_DETAIL, { id: call.id })}>
          <div className="call-header">
            <div className="call-type">
              <div className="eu-flag">
                <img src={call.programme.logo} alt="EU call" />
              </div>
              <span>{call.programme.name}</span>
            </div>
            <button
              className="bookmark-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBookmark();
              }}
            >
              <SvgIcon
                icon={
                  call.is_call_bookmarked ? "icon-bookmarked" : "icon-bookmark"
                }
              />
            </button>
          </div>

          <div className="call-badges">
            {call.is_new && <span className="badge new-badge">New</span>}
            <span className="badge deadline-badge">
              <SvgIcon icon="icon-clock" />
              until{" "}
              {new Date(call.deadline).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="badge days-left-badge">{daysLeft} days left</span>
          </div>

          <h4 className="call-title">
            <Link to={createRoute(ROUTES.CALL_DETAIL, { id: call.id })}>
              {call.title}
            </Link>
          </h4>

          <div className="call-budget">
            € {parseInt(call.budget).toLocaleString()}
          </div>

          <div className="idea-meta">
            <div className="partnership-section">
              <div className="partnership-logos ">
                <span className="label">Interested Partners</span>
                {typeof userBasicInfo?.partner === "string" &&
                userBasicInfo.partner === "freelancer" ? (
                  <>
                    {call?.interested_partners && (
                      <Tags
                        type={"partner"}
                        number={call?.interested_partners.length}
                        className="small"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {(() => {
                      if (!call?.interested_partners) return null;

                      const partnerships = Array.isArray(
                        call?.interested_partners,
                      )
                        ? call?.interested_partners
                        : Object.values(call?.interested_partners).map(
                            (entry) => entry,
                          );

                      return (
                        <>
                          {partnerships
                            .slice(0, 4)
                            .map((partner: any, i: number) => (
                              <div
                                key={partner?.id}
                                className="partner-logo placeholder"
                                style={{
                                  transform:
                                    i > 0 ? `translateX(-${10 * i}px)` : "none",
                                  zIndex: i > 0 ? 2 + (i - 1) : 1,
                                }}
                              >
                                <BgImage
                                  img={partner.logo_uri}
                                  altImage={imageList.noOrgimage}
                                />
                              </div>
                            ))}
                          {partnerships.length > 4 && (
                            <span
                              className="more-count-small"
                              style={{
                                transform: `translateX(-${10 * partnerships.slice(0, 4).length}px)`,
                                zIndex: 2 + partnerships.length,
                              }}
                            >
                              +{partnerships.length - 4}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="call-sectors">
            {call.sectors?.slice(0, 3).map((sector, index) => (
              <span key={index} className="sector-tag">
                {typeof sector === "string" ? sector : sector.name}
              </span>
            ))}
            {call.sectors?.length > 3 && (
              <span className="sector-count">+{call.sectors.length - 3}</span>
            )}
          </div>

          <div className="call-footer">
            {typeof call?.max_score === "string" && userBasicInfo?.pro_user ? (
              <div
                className="match-badge"
                style={{
                  backgroundColor: getScoreColor(call.max_score),
                }}
              >{`${call?.max_score}% match`}</div>
            ) : (
              <div>&nbsp;</div>
            )}
            <div className="call-stats">
              <span className="stat-item">
                <SvgIcon icon="icon-message" />
                {call.comments_count || 5} comments
              </span>
              <span className="stat-item">
                <SvgIcon icon="icon-ideas" />
                {call.ideas_count || 3} ideas
              </span>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};
